import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { RegistrationStammdatenPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { CsjensblanksDesiredDegreeEnum, CsjensblanksGenderEnum } from './csjensblank-stammdaten-plugin.js';

// ===== Schlüsselkompetenzen =====
// Same field set as the Career Service Goethe-Uni plugin (csjensblank), but Nationalität and
// Geburtsland are optional here. Gender-, Abschluss-Enum and the Fachbereich list are reused from
// csjensblank so their labels stay in one place.

export const schluesselkompetenzenRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::schluesselkompetenzen',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new SchluesselkompetenzenStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(SchluesselkompetenzenStammdaten.fields).filter(
      it => !['_id', '_class', 'userID', 'institutionID'].includes(it),
    );
  },
  classType: () => SchluesselkompetenzenStammdaten,
};

@Entity()
export class SchluesselkompetenzenStammdaten extends UserStammdaten {
  static override _class = 'SchluesselkompetenzenStammdaten';

  override getTranslationNamespace() {
    return 'schluesselkompetenzen';
  }

  @validate(nonOptional())
  @Field(type => String)
  matriklNr: Maybe<string>;

  // 1. Persönliche Angaben
  // Geschlecht: männlich/ weiblich/ divers/ ohne Angabe
  @validate(nonOptional())
  @Field(type => CsjensblanksGenderEnum)
  gender: Maybe<CsjensblanksGenderEnum>;

  // Staatsangehörigkeit: (Drop Down alle Länder) – optional
  @Field(type => String)
  nationality: Maybe<string>;

  // Geburtsland: (Drop Down alle Länder) – optional
  @Field(type => String)
  countryOfBirth: Maybe<string>;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => String, { default: value => value ?? 'DE' })
  uniQualificationCountry: Maybe<string>;

  // Fachsemester:
  @validate(nonOptional())
  @Field(type => String)
  semester: Maybe<string>;

  // Studiengang:
  @validate(nonOptional())
  @Field(type => String)
  courseOfStudy: Maybe<string>;

  // Fachbereich:
  @validate(nonOptional())
  @Field(type => String)
  department: Maybe<string>;

  // Angestrebter Abschluss:
  @validate(nonOptional())
  @Field(type => CsjensblanksDesiredDegreeEnum)
  desiredDegree: Maybe<CsjensblanksDesiredDegreeEnum>;

  @Field(type => Boolean, { default: value => value ?? false })
  newsletter!: boolean;
}
